import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  UserCheck, 
  UserX, 
  Clock,
  Plus,
  RefreshCw,
  Eye,
  Calendar
} from 'lucide-react';
import { useReseller } from '@/hooks/useReseller';
import { cn } from '@/utils/functions/cn';

export const ResellerDashboard = () => {
  const { dashboard, isDashboardLoading, refetchDashboard } = useReseller();

  if (isDashboardLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Carregando dashboard...</span>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Erro ao carregar dados do dashboard</p>
        <Button onClick={() => refetchDashboard()} className="mt-4">
          Tentar Novamente
        </Button>
      </div>
    );
  }

  const { profile, stats } = dashboard;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'default',
      suspended: 'destructive',
      pending: 'secondary'
    } as const;
    
    const labels = {
      active: 'Ativo',
      suspended: 'Suspenso',
      pending: 'Pendente'
    };
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {labels[status as keyof typeof labels] || status}
      </Badge>
    );
  };

  const getTierBadge = (tier: string) => {
    const variants = {
      basic: 'secondary',
      premium: 'default',
      enterprise: 'destructive'
    } as const;
    
    const labels = {
      basic: 'Básico',
      premium: 'Premium',
      enterprise: 'Enterprise'
    };
    
    return (
      <Badge variant={variants[tier as keyof typeof variants] || 'secondary'}>
        {labels[tier as keyof typeof labels] || tier}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard Reseller</h1>
          <p className="text-muted-foreground">
            Bem-vindo de volta, {profile.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {getTierBadge(profile.tier)}
          {getStatusBadge(profile.status)}
        </div>
      </div>

      {/* Profile Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Informações do Perfil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Nome</p>
              <p className="font-medium">{profile.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{profile.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Empresa</p>
              <p className="font-medium">{profile.company || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Comissão</p>
              <p className="font-medium">{profile.commission}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Clientes</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalClients}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeClients} ativos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Mensal</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.monthlyRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              Comissão: {formatCurrency(stats.monthlyEarnings)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.conversionRate}%</div>
            <Progress value={stats.conversionRate} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ganhos Totais</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalEarnings)}</div>
            <p className="text-xs text-muted-foreground">
              Desde {new Date(profile.joinedAt).toLocaleDateString('pt-BR')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Client Status Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Status dos Clientes</CardTitle>
            <CardDescription>
              Distribuição dos clientes por status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-green-500" />
                <span>Ativos</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{stats.activeClients}</span>
                <Badge variant="default">{((stats.activeClients / stats.totalClients) * 100).toFixed(1)}%</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserX className="h-4 w-4 text-red-500" />
                <span>Suspensos</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{stats.suspendedClients}</span>
                <Badge variant="destructive">{((stats.suspendedClients / stats.totalClients) * 100).toFixed(1)}%</Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-orange-500" />
                <span>Expirados</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{stats.expiredClients}</span>
                <Badge variant="secondary">{((stats.expiredClients / stats.totalClients) * 100).toFixed(1)}%</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Clientes Recentes</CardTitle>
            <CardDescription>
              Últimos clientes adicionados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.recentClients.slice(0, 5).map((client) => (
                <div key={client.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="text-left">
                    <p className="font-medium text-left">{client.name}</p>
                    <p className="text-sm text-muted-foreground text-left">{client.email}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                      {client.status}
                    </Badge>
                    <p className="text-sm text-muted-foreground mt-1">{client.plan}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <Button variant="outline" className="w-full mt-4">
              <Eye className="h-4 w-4 mr-2" />
              Ver Todos os Clientes
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Ações Rápidas</CardTitle>
          <CardDescription>
            Ferramentas mais utilizadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button className="h-20 flex-col gap-2" onClick={() => window.location.href = '/reseller/clients/new'}>
              <Plus className="h-6 w-6" />
              <span>Criar Cliente</span>
            </Button>
            
            <Button variant="outline" className="h-20 flex-col gap-2">
              <Calendar className="h-6 w-6" />
              <span>Gerar Códigos</span>
            </Button>
            
            <Button variant="outline" className="h-20 flex-col gap-2">
              <TrendingUp className="h-6 w-6" />
              <span>Relatórios</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
